import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { digestExamplesPath, githubDigestPath, rootPath, signInPath, whatIsAGithubDigestPath } from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const SIGN_IN_CAMPAIGN = 'utm_source=weekly-git-commit-digest&utm_campaign=stakeholders'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

function TextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className={linkClass}>
      {children}
    </Link>
  )
}

export function WeeklyGitCommitDigestArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">Weekly digest</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          An AI weekly git commit digest is a business progress summary of what landed in GitHub. It is for a
          non-technical stakeholder: a founder, a product manager, an executive, or a client who does not read the
          code. <TextLink href={rootPath()}>Git Digest</TextLink> writes that summary from the repositories you select
          and posts it to Slack and email.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 text-center sm:p-8">
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
          The useful line says what changed for the business. A file name, a function name, or a diff is not what this
          audience needs.
        </p>
      </Card>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What a weekly git commit digest is</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Sign in with GitHub and choose the repositories that belong in the digest. Only those repos are included.
            Set the cadence to weekly. A weekly digest covers the past week and is prepared on Mondays. A daily digest
            covers the previous 24 hours. Weekly is the cadence when the reader wants one summary, not a morning round.
          </p>
          <p>
            Email goes to workspace members who have the email digest turned on, at the email hour you set. Slack goes
            to the channel stored when you connect Slack, at the Slack hour you set. A founder, an executive, or a
            client in that channel gets the same post. Digests are prepared for workspaces with an active
            subscription.
          </p>
          <p>
            A client who should not see the rest of the workspace can open a public changelog for one repository
            instead. That page does not require a sign-in, and it does not show the other repositories. The{' '}
            <TextLink href={whatIsAGithubDigestPath()}>definition of a GitHub digest</TextLink> explains that link,
            the daily and weekly windows, and what each line contains.
          </p>
          <p>
            Some results for a weekly git commit digest describe Gitingest, which turns a repository into text for a
            language model. Git Digest summarizes what landed and posts that summary to people. The{' '}
            <TextLink href={githubDigestPath()}>GitHub digest comparison</TextLink> is the side-by-side of those two
            products.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What a non-technical reader should see</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            A good line names the outcome. It says who can do something new, which problem stopped, or which workflow
            changed. It does not name a controller, a migration, or a function.
          </p>
          <p>
            Git Digest asks the model to write an ordinary summary in under 140 characters, in words a non-technical
            reader can follow, and to put the category in a tag instead of at the start of the sentence. The tags are
            Feature, Bugfix, Improvement, Chore, and Refactoring. A change that touches more than a dozen files can
            run longer and include a short list. The model is told to read the file changes first, then the pull
            request description, then the commit message, and to leave out technical words.
          </p>
          <p>
            Slack groups each line under its first tag. The header is the date range. The line is the summary, then
            the pull request number or a short commit id, then the author. Email shows the first five changes in each
            repository, with lines added and removed. Those counts are there if someone wants them. The sentence is
            the part a stakeholder reads. If the week is empty, Slack says there were no code changes.
          </p>
          <p>
            Real summary lines from public changelogs, annotated for an engineering manager, are on the{' '}
            <TextLink href={digestExamplesPath()}>digest examples</TextLink> page. The example below is separate. It
            is made up, so you can see the shape without reading a customer&apos;s work.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">A made-up weekly example</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            This example is written for this page. The repository names, people, pull request numbers, and sentences
            are invented. It is not a customer digest, and it is not copied from a public changelog.
          </p>
        </div>
        <Card>
          <CardContent className="space-y-6 pt-6">
            <p className="text-foreground text-sm font-semibold tracking-wide uppercase">Made-up example</p>
            <p className="text-foreground text-base font-medium">Your Git Digest for Mar 2 - Mar 8, 2026</p>
            <div className="space-y-3">
              <h3 className="text-foreground text-lg">Invoices</h3>
              <p className="text-foreground text-sm font-semibold">Feature</p>
              <ul className="text-muted-foreground list-disc space-y-2 pl-5 text-sm leading-relaxed">
                <li>
                  Customers can pay an open invoice from the email, so finance does not resend the link. (#42) by
                  Alex.
                </li>
              </ul>
              <p className="text-foreground text-sm font-semibold">Bugfix</p>
              <ul className="text-muted-foreground list-disc space-y-2 pl-5 text-sm leading-relaxed">
                <li>
                  A declined card no longer drops the order when the buyer tries a second card. (#47) by Casey.
                </li>
              </ul>
            </div>
            <div className="space-y-3">
              <h3 className="text-foreground text-lg">Trials</h3>
              <p className="text-foreground text-sm font-semibold">Feature</p>
              <ul className="text-muted-foreground list-disc space-y-2 pl-5 text-sm leading-relaxed">
                <li>
                  New trials can enter a company name, so sales can tell one signup from another. (#44) by Blair.
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            A stakeholder can read those three lines and say what moved: invoices, checkout, and trial records. They
            do not need the diff. A real Slack message uses that shape, with the date range as the header and each
            line linked to GitHub. A real email uses the same sentences, adds lines added and removed, and stops at
            five changes per repository. The from address on the weekly email is weekly@updates.gitdigest.ai.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">How Git Digest produces it</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Git Digest reads the commits that landed in the selected repositories during the week. For each change it
            sends the patch, the pull request description when there is one, and the commit message to a model. The
            model returns the short summary and one or more tags. Source code is not stored. The digest is built from
            the summary, the tags, the author, the GitHub link, and the line counts.
          </p>
          <p>
            The weekly send is prepared on Mondays, at the email hour and the Slack hour you set, in the workspace
            timezone. The subject and the Slack header are the date range, in the form used in the example above. If
            nothing landed in that window, Slack says there were no code changes.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">
          When the weekly digest replaces a status meeting
        </h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Send the digest when the engineering update is already in GitHub. The team does not retype the week into
            a slide, a standup form, or a client email. A manual update can leave out a commit, or describe work that
            did not land. The digest is limited to the repositories you picked and to the changes that landed in that
            week.
          </p>
          <p>
            Keep a short meeting when the work never becomes a commit. Hiring, a customer call, and a decision that
            is still only a conversation are not in the digest. A form bot is a better fit for work that is not in
            GitHub.
          </p>
          <p>
            A summary of one pull request is a different artifact. It helps the person reviewing that pull request. It
            does not roll the week up across people and repositories for a founder, an executive, or a client.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Weekly git commit digest FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Short answers for a non-technical stakeholder who wants a weekly business progress summary.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Try a weekly Git Digest</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub, pick the repositories, and send a weekly summary to the people who do not read the code.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={`${signInPath()}?${SIGN_IN_CAMPAIGN}`}>
            Try Git Digest
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
